"use client";

import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { usePathname, useRouter } from "apps/website/i18n/navigation";
import {
  SignupFlowSlug,
} from "libs/state/src/lib/stores/flows/fresh/signupFlow/constants";
import { useSignupFlowStore, getRehydrationRedirect } from "@./state";
import {
  buildRedirectUrl,
} from "apps/website/utils/abandoned-cart/buildRedirectUrl";

const INITIAL_SLUG = SignupFlowSlug.CAT_COUNT;
const BYPASS_PARAM = "kk";

type GuardState =
  | { status: "idle" }
  | { status: "redirecting"; target: string }
  | { status: "done" };

/**
 * Rehydration guard for organic returning users (localStorage hydration).
 *
 * CTA entry (initial slug): checks critical fields before RECIPES and
 * redirects to CHECKOUT or RECIPES when nothing is incomplete.
 *
 * Direct-link entry (non-initial slug): checks critical fields before the
 * page the user navigated to. Stays on that page if nothing is incomplete,
 * or moves to the next accessible page if the page is inaccessible.
 *
 * Returns `isGuardComplete` so the layout can hold the loading screen until
 * the guard has finished evaluating.
 */
export function useSignupFlowRehydrationGuard(): { isGuardComplete: boolean } {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [ isGuardComplete, setIsGuardComplete ] = useState(false);
  // Lives with the flow layout, so it persists across in-flow navigation and
  // StrictMode's simulated remount, and resets when the user leaves the SUF.
  const guardStateRef = useRef<GuardState>({ status: "idle" });

  const {
    isStoreInitialised,
    getActiveCatIds,
  } = useSignupFlowStore((state) => ({
    isStoreInitialised: state.isStoreInitialised,
    getActiveCatIds: state.getActiveCatIds,
  }));

  useEffect(() => {
    const guardState = guardStateRef.current;
    const setGuardState = (next: GuardState): void => {
      guardStateRef.current = next;
    };

    if (!isStoreInitialised) {
      return;
    }

    if (guardState.status === "done") {
      setIsGuardComplete(true);
      return;
    }

    // A redirect is in flight (StrictMode re-fire or waiting for nav).
    // Keep the loading screen until the pathname matches the target.
    if (guardState.status === "redirecting") {
      if (pathname === guardState.target) {
        setGuardState({ status: "done" });
        setIsGuardComplete(true);
      }
      return;
    }

    // The ?kk param lets internal users bypass the guard entirely.
    if (searchParams.has(BYPASS_PARAM)) {
      setGuardState({ status: "done" });
      setIsGuardComplete(true);
      return;
    }

    // New users on the start page have no store data to validate.
    if (pathname === INITIAL_SLUG && getActiveCatIds().length === 0) {
      setGuardState({ status: "done" });
      setIsGuardComplete(true);
      return;
    }

    // CTA entry: check before RECIPES, fallback to RECIPES or CHECKOUT.
    // Direct-link entry: check before the current page, or the next accessible
    // page if the current one is inaccessible. No RECIPES/CHECKOUT fallback.
    const isCta = pathname === INITIAL_SLUG;
    const options = isCta
      ? undefined
      : { targetSlug: pathname as SignupFlowSlug };

    const { slug } = getRehydrationRedirect(undefined, options);

    if (slug && slug !== pathname) {
      setGuardState({ status: "redirecting", target: slug });
      router.replace(buildRedirectUrl(slug, searchParams));
      return;
    }

    setGuardState({ status: "done" });
    setIsGuardComplete(true);
  }, [
    isStoreInitialised,
    pathname,
    router,
    searchParams,
    getActiveCatIds,
  ]);

  return { isGuardComplete };
}
